"use client";
import type {
  SessionProgress,
  SetSessionProgress,
} from "../../../types/audit-form";

import type { AuditFileMetadata } from "../../../types/audit-file";
import { useLatest } from "../../../hooks/use-latest";
import { useImageDescriptions } from "../../../hooks/use-image-descriptions";
import {
  errorMessage,
  requireMutationSuccess,
} from "../../../utils/mutationResult.js";
import { OfflineImage } from "../../shared/offline-image";

import {
  Download,
  Eye,
  FileText,
  Loader2,
  Plus,
  Save,
  Trash2,
  X,
} from "lucide-react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDebouncedSave } from "../../../hooks/use-debounced-save";
import { useDraftAutosave } from "../../../hooks/use-draft-autosave";
import {
  useVersionAwareFiles,
  useVersionAwareSiteData,
  useVersionAwareWorks,
} from "../../../hooks/use-version-aware-data";
import { useNotification } from "../../../hooks/useNotification";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  deleteFile,
  fetchFiles,
  updateFile,
  uploadMultipleFiles,
} from "../../../redux/slices/fileSlice.js";
import { updateSiteData } from "../../../redux/slices/siteDataSlice.js";
import {
  createWork,
  deleteWork,
  fetchWorks,
  updateWork,
} from "../../../redux/slices/workSlice.js";
import { punchListKeyActivitiesPerformedProgress } from "../../../utils/componentUtils.js";
import { notificationHelpers } from "../../../utils/notificationHelpers";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button.jsx";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card.jsx";
import Checkbox from "../../ui/checkbox.jsx";
import { Label } from "../../ui/label.jsx";
import { LoadingIndicator } from "../../ui/loading-indicator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";
import {
  OptimizedInput,
  OptimizedSelect,
  OptimizedTextarea,
} from "../optimized-form-field";
import { ImageCapturePicker } from "../shared/image-capture-picker";
import { LocalSectionImportControl } from "../shared/local-section-import-control";

interface FileAttachment extends AuditFileMetadata {
  id: string;
  fileName: string;
  fileUrl: string;
  fileType: string;
  fileSize: number;
  uploadedDate: string;
  description?: string;
  name?: string;
}

interface Activity {
  id: string;
  workCategory: "mechanical" | "electrical" | "civil" | "other";
  activityName: string;
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  responsibleParty: string;
  status: "completed" | "in-progress" | "pending";
  issuesEncountered: string;
  attachments: FileAttachment[];
  relatedWorks?: string[];
  comments?: string;
  workType?: string;
  customCategory?: string;
}

interface WorkItem extends Activity {
  _id?: string;
  [key: string]: any;
}

interface KeyActivitiesData {
  activities: Activity[];
  overallComment: string;
  comments: string; // Additional comments per activity
  overallSummary: string; // Summary of all key activities
}

// Predefined work titles based on your information
const PREDEFINED_WORKS = {
  mechanical: [
    "Purlin and other attachments",
    "Galvanization",
    "Quality of piling/ramming the post",
    "Module misalignment",
    "End Protection",
    "Mounting Structure",
    "Underground Cable trough",
    "Condition of modules",
    "Mechanical corrosion",
    "Torque Nuts and bolts",
    "Expansion holes",
    "Perimeter Fencing",
  ],
  electrical: [
    "Grounding and Earthing",
    "Cable Management",
    "Inverter Installation",
  ],
  civil: [
    "How is the onsite traffic management",
    "are the vehicles and machineries suitable for the site conditions",
    "Trenches Works",
    "Transformer Foundation",
    "DNO S/S",
    "Site cleanliness",
    "Foundations",
    "Onsite traffic management",
    "Vehicles and machineries suitability",
    "Additional Information on the Present State and the Current Use of the Land",
  ],
  other: [],
};

const initialData: KeyActivitiesData = {
  activities: [],
  overallComment: "",
  comments: "",
  overallSummary: "",
};

export const KeyActivitiesPerformed = memo(function KeyActivitiesPerformed({
  siteId,
  templateId,
  reportId,
  setSessionProgress,
  readOnly = false,
  previousCompletedReports = [],
}: {
  siteId: string;
  templateId: string | null;
  reportId: string | null;
  sessionProgress: SessionProgress;
  setSessionProgress: SetSessionProgress;
  readOnly?: boolean;
  isViewingHistoricalVersion?: boolean;
  versionData?: any;
  previousCompletedReports?: any[];
}) {
  const { notify } = useNotification();

  const [formData, setFormData] = useState<KeyActivitiesData>(initialData);
  const formDataRef = useLatest(formData);
  const [activityData, setActivityData] = useState<
    Record<string, Partial<WorkItem>>
  >({});
  const [activeTab, setActiveTab] = useState<
    "mechanical" | "electrical" | "civil" | "other" | "all"
  >("all");
  const [selectedFile, setSelectedFile] = useState<FileAttachment | null>(null);
  const [showCustomInput, setShowCustomInput] = useState<{
    [key: string]: boolean;
  }>({});

  const [uploadingWorkImages, setUploadingWorkImages] = useState<
    Record<string, boolean>
  >({});
  const [workProgressData, setWorkProgressData] = useState([]);

  const { loading } = useAppSelector((state: any) => state?.files);
  const files = useVersionAwareFiles(readOnly);
  const [imageDescriptions, setImageDescriptions] = useImageDescriptions(files);

  const dispatch = useAppDispatch();

  const currentSiteData = useVersionAwareSiteData(readOnly);
  const currentWorksData = useVersionAwareWorks(readOnly);

  // Autosave unsaved edits to IndexedDB so nothing is lost if the app closes
  // before the debounced server save fires (e.g. offline, tab closed, phone
  // loses power).
  const draftKey =
    !readOnly && reportId
      ? `report:${reportId}:key-activities-performed`
      : null;
  const { restoreDraft, clearSavedDraft } = useDraftAutosave(
    draftKey,
    formData,
  );
  const draftRestoredRef = useRef(false);

  // Restore any unsaved draft once on mount, before server data can
  // overwrite it below.
  useEffect(() => {
    if (!draftKey || draftRestoredRef.current) return;
    draftRestoredRef.current = true;
    restoreDraft().then((draft) => {
      if (draft?.data) {
        setFormData((prev) => ({ ...prev, ...draft.data }));
      }
    });
  }, [draftKey, restoreDraft]);

  // Once local form state matches the confirmed server record, the
  // IndexedDB draft is redundant — clear it so a future mount doesn't
  // restore stale local data over a fresher server record.
  useEffect(() => {
    if (!draftKey || !currentSiteData) return;
    if (JSON.stringify(currentSiteData) === JSON.stringify(formData)) {
      clearSavedDraft();
    }
  }, [draftKey, currentSiteData, formData, clearSavedDraft]);

  useEffect(() => {
    dispatch(fetchWorks({ siteId, templateId, reportId }) as any);
    dispatch(fetchFiles({ siteId, reportId }) as any);
  }, [dispatch, siteId, templateId, reportId]);

  useEffect(() => {
    const progress = punchListKeyActivitiesPerformedProgress(
      workProgressData,
      formData?.overallSummary,
    );

    setSessionProgress((prev) => ({
      ...prev,
      [templateId || "punch-list"]: {
        ...(prev[templateId || "punch-list"] || {}),
        "key-activities-performed": progress,
      },
    }));
  }, [formData, workProgressData, templateId, files, setSessionProgress]);

  const handleSaveWork = useCallback(
    async (data: typeof activityData) => {
      try {
        const importedWorkEntries = workProgressData
          .filter((work: any) => work?._sourceId && !work?._id)
          .map((work: any) => [work.id, work] as [string, Partial<WorkItem>]);
        const workEntries = [
          ...Object.entries(data),
          ...importedWorkEntries.filter(([workId]) => !data[workId]),
        ];

        const mutationResult1 = await Promise.all(
          workEntries.map(([workId, pendingWorkData]) => {
            const existingWork = workProgressData.find(
              (work: any) => work?._id === workId || work?.id === workId,
            );
            const { _id, ...existingWorkData } = existingWork || {};
            const isImportedWork =
              String(workId).startsWith("imported-") || !existingWork?._id;
            const payload = {
              ...(existingWorkData || {}),
              ...pendingWorkData,
              siteId,
              templateId,
              workCategory: "key-activities-performed",
            };

            return isImportedWork
              ? requireMutationSuccess(dispatch(createWork(payload as any)))
              : dispatch(updateWork({ workId, workData: payload })).unwrap();
          }),
        );
        const config = notificationHelpers.formSaveSuccess("Work Data", [
          mutationResult1,
        ]);
        notify(config.message, config.type, config.options);
      } catch (error) {
        const config = notificationHelpers.formSaveError(
          "Work Data",
          errorMessage(error),
        );
        notify(config.message, config.type, config.options);
        throw error;
      }
    },
    [dispatch, notify, workProgressData, siteId, templateId],
  );

  const handleSaveForm = useCallback(
    async (data: typeof formData) => {
      if (!siteId) return;

      try {
        const mutationResult2 = await requireMutationSuccess(
          dispatch(
            updateSiteData({
              siteDataId: siteId,
              siteDataInfo: {
                reportId,
                punchList: {
                  keyActivitiesPerformed: {
                    comments: data.comments || "",
                    overallSummary: data.overallSummary || "",
                    overallComment: data.overallComment || "",
                  },
                },
              },
            } as any),
          ),
        );
        const config = notificationHelpers.formSaveSuccess("Form Data", [
          mutationResult2,
        ]);
        notify(config.message, config.type, config.options);
      } catch (error) {
        const config = notificationHelpers.formSaveError(
          "Form Data",
          errorMessage(error),
        );
        notify(config.message, config.type, config.options);
        throw error;
      }
    },
    [dispatch, siteId, reportId, notify],
  );

  const getLoadingStatus = () => {
    if (saveError) return "error";
    if (isSaving) return "saving";
    if (hasUnsavedChanges) return "unsaved";
    if (lastSaved) return "saved";
    return "idle";
  };

  const {
    isSaving: isWorkSaving,
    lastSaved: lastSavedWork,
    hasUnsavedChanges: hasWorkUnsavedChanges,
    saveError: workSaveError,
    forceSave: forceWorkSave,
  } = useDebouncedSave(activityData, {
    delay: 5000,
    onSave: handleSaveWork,
    onError: (error) => console.error("Work save error:", error),
    readOnly,
  });

  const {
    resetBaseline: formResetBaseLine,
    isSaving: isFormSaving,
    lastSaved: lastSavedForm,
    hasUnsavedChanges: hasFormUnsavedChanges,
    saveError: formSaveError,
    forceSave: forceFormSave,
  } = useDebouncedSave(formData, {
    delay: 5000,
    onSave: handleSaveForm,
    onError: (error) => console.error("Form save error:", error),
    readOnly,
  });

  useEffect(() => {
    const keyActivitiesData =
      currentSiteData?.punchList?.keyActivitiesPerformed || {};

    const newFormData = { ...formDataRef.current, ...keyActivitiesData };
    formResetBaseLine(newFormData);
    setFormData(newFormData);
    setWorkProgressData(
      currentWorksData?.filter(
        (w: any) => w.workCategory === "key-activities-performed",
      ) || [],
    );
  }, [currentSiteData, currentWorksData, formResetBaseLine, formDataRef]);

  // Save handler for file descriptions
  const handleSaveImages = useCallback(
    async (descriptions: Record<string, string>) => {
      try {
        const mutationResult3 = await Promise.all(
          Object.entries(descriptions).map(([fileId, fileDescription]) =>
            requireMutationSuccess(
              dispatch(updateFile({ fileId, fileData: { fileDescription } })),
            ),
          ),
        );
        const config = notificationHelpers.formSaveSuccess(
          "Image Descriptions",
          [mutationResult3],
        );
        notify(config.message, config.type, config.options);
      } catch (error) {
        const config = notificationHelpers.formSaveError(
          "Image Descriptions",
          errorMessage(error),
        );
        notify(config.message, config.type, config.options);

        throw error;
      }
    },
    [dispatch, notify],
  );

  // Debounced save for file descriptions (5000ms delay)
  const {
    isSaving: isImageSaving,
    lastSaved: lastSavedImage,
    hasUnsavedChanges: hasImageUnsavedChanges,
    saveError: imageSaveError,
    forceSave: forceImageSave,
  } = useDebouncedSave(imageDescriptions, {
    delay: 5000,
    onSave: handleSaveImages,
    onError: (error) => console.error("Image description save error:", error),
    readOnly,
  });

  // Combine form and image save states
  const isSaving = isFormSaving || isWorkSaving || isImageSaving;
  const hasUnsavedChanges =
    hasFormUnsavedChanges || hasWorkUnsavedChanges || hasImageUnsavedChanges;
  const saveError = formSaveError || workSaveError || imageSaveError;
  const lastSaved = useMemo(() => {
    const dates = [lastSavedForm, lastSavedImage, lastSavedWork].filter(
      Boolean,
    );
    if (dates.length === 0) {
      return null;
    }

    return new Date(Math.max(...dates.map((date) => new Date(date).getTime())));
  }, [lastSavedForm, lastSavedWork, lastSavedImage]);

  const forceSave = useCallback(() => {
    forceFormSave();
    forceWorkSave();

    forceImageSave();
  }, [forceFormSave, forceWorkSave, forceImageSave]);
  // const forceSave = () => {
  //   workSave.forceSave();
  //   formSave.forceSave();
  // };

  // File upload handler
  const handleFileUpload = (
    files: FileList | File[] | null,
    filterData: any,
    entityId?: string,
  ) => {
    if (!files) return;

    const uploadKey = JSON.stringify(filterData);
    setUploadingWorkImages((prev) => ({ ...prev, [uploadKey]: true }));

    const fileArray = Array.from(files);

    requireMutationSuccess(
      dispatch(
        uploadMultipleFiles({
          files: fileArray,
          metadata: {
            siteId,
            entityType: `key-activities-performed`,
            entityId: entityId || reportId,
            reportId,
          },
          // filterData,
        }),
      ),
    )
      .then((mutationOutcome) => {
        // Show success notification for each uploaded file
        fileArray.forEach((file) => {
          const config = notificationHelpers.imageUploadSuccess(
            file.name,
            mutationOutcome,
          );
          notify(config.message, config.type, config.options);
        });
      })
      .catch((error) => {
        // Show error notification
        const config = notificationHelpers.imageUploadError(
          fileArray.length === 1
            ? fileArray[0].name
            : `${fileArray.length} files`,
          errorMessage(error),
        );
        notify(config.message, config.type, config.options);
      })
      .finally(() => {
        setUploadingWorkImages((prev) => ({ ...prev, [uploadKey]: false }));
      });
  };

  const removeFile = (fileId: string) => {
    requireMutationSuccess(dispatch(deleteFile(fileId) as any))
      .then((mutationOutcome) => {
        const config = notificationHelpers.imageDeleteSuccess(
          "Image",
          mutationOutcome,
        );
        notify(config.message, config.type, config.options);
      })
      .catch((error) => {
        const config = notificationHelpers.imageDeleteError(
          "Image",
          errorMessage(error),
        );
        notify(config.message, config.type, config.options);
      });
  };

  // Update file description - updates local state; debounced save handles the dispatch
  const updateImageDescription = useCallback(
    (fileId: string, description: string) => {
      setImageDescriptions((prev) => ({ ...prev, [fileId]: description }));
    },
    [setImageDescriptions],
  );

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return (
      Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
    );
  };

  const getFilesByEntity = (filterData: any, entityId?: string) => {
    return files.filter(
      (file: any) =>
        file?.relatedTo?.entityType === `key-activities-performed` &&
        (!entityId || file?.relatedTo?.entityId === entityId),
      //  &&
      // file?.relatedTo?.filterData &&
      // JSON.stringify(file?.relatedTo?.filterData) ===
      //   JSON.stringify(filterData),
    );
  };
  const addActivity = async (
    category: "mechanical" | "electrical" | "civil" | "other" = "mechanical",
  ) => {
    try {
      await requireMutationSuccess(
        dispatch(
          createWork({
            siteId,
            templateId,
            title: "",
            description: "",
            name: "",
            category,

            workType: category,
            workCategory: "key-activities-performed",
            status: "pending",
          } as any),
        ),
      )
        .then((mutationOutcome) => {
          const config = notificationHelpers.actionCreate(
            "Activity",
            mutationOutcome,
          );
          notify(config.message, config.type, config.options);
        })
        .catch((error) => {
          const config = notificationHelpers.actionCreateError(
            "Activity",
            errorMessage(error),
          );
          notify(config.message, config.type, config.options);
        });
    } catch (error) {
      const config = notificationHelpers.actionCreateError(
        "Activity",
        errorMessage(error),
      );
      notify(config.message, config.type, config.options);
    }
  };

  const removeActivity = async (id: string) => {
    try {
      await requireMutationSuccess(dispatch(deleteWork(id) as any))
        .then((mutationOutcome) => {
          const config = notificationHelpers.actionDelete(
            "Activity",
            mutationOutcome,
          );
          notify(config.message, config.type, config.options);
        })
        .catch((error) => {
          const config = notificationHelpers.actionDeleteError(
            "Activity",
            errorMessage(error),
          );
          notify(config.message, config.type, config.options);
        });
    } catch (error) {
      const config = notificationHelpers.actionDeleteError(
        "Activity",
        errorMessage(error),
      );
      notify(config.message, config.type, config.options);
    }
  };
  const updateActivity = (
    id: string,
    field: keyof Activity | string,
    value: any,
  ) => {
    setWorkProgressData((prev) =>
      prev.map((work) =>
        work.id === id || work._id === id
          ? {
              ...work,
              [field]: value,
            }
          : work,
      ),
    );
    setActivityData((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value,
      },
    }));
  };

  const toggleCustomInput = (activityId: string) => {
    setShowCustomInput((prev) => ({
      ...prev,
      [activityId]: !prev[activityId],
    }));
  };

  const getActivityOptions = (
    category: "mechanical" | "electrical" | "civil" | "other",
  ) => {
    const predefinedOptions =
      PREDEFINED_WORKS[category]?.map((work: any) => ({
        value: work,
        label: work,
      })) || [];

    return predefinedOptions;
  };
  const getWorkValue = <K extends keyof WorkItem>(
    item: WorkItem,
    field: K,
  ): WorkItem[K] =>
    field in (activityData[item.id] || {})
      ? (activityData[item.id][field] as WorkItem[K])
      : item[field];
  // Helper function to check if any upload is in progress for a specific section
  const isUploadingForSection = (filterData: any) => {
    const uploadKey = JSON.stringify(filterData);
    return uploadingWorkImages[uploadKey] || false;
  };

  const filteredActivities =
    activeTab === "all"
      ? currentWorksData?.filter(
          (w) => w.workCategory === "key-activities-performed",
        ) || []
      : currentWorksData
          ?.filter((w) => w.workCategory === "key-activities-performed")
          ?.filter((activity) => activity.workType === activeTab);

  const renderFileAttachments = (
    filterData: any,
    entityId?: string,
    required = false,
  ) => {
    const entityFiles = getFilesByEntity(filterData, entityId);
    const isUploading = isUploadingForSection(filterData);

    return (
      <div className="space-y-3">
        {isUploading && (
          <div className="flex items-center justify-center py-6 border border-dashed border-slate-300 rounded-lg bg-slate-50 dark:bg-muted dark:border-border">
            <div className="text-center">
              <Loader2 className="w-6 h-6 text-slate-400 animate-spin mx-auto mb-2 dark:text-muted-foreground" />
              <p className="text-sm text-slate-600 dark:text-muted-foreground">Uploading images...</p>
            </div>
          </div>
        )}
        {/* <Label className="text-sm font-medium flex items-center gap-2">
          <FileText className="w-4 h-4" />
          Supporting Documents & Photos {required && <span className="text-red-500">*</span>}
        </Label> */}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {entityFiles.map((file: any) => (
            <div key={file.id} className="relative group">
              {file?.fileType?.startsWith("image/") ? (
                <div className="aspect-video bg-slate-100 rounded overflow-hidden dark:bg-muted">
                  <OfflineImage
                    fileId={file?._id || file?.id || file?._clientId}
                    src={file.fileUrl}
                    alt={file.originalName}
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="aspect-video bg-slate-100 rounded flex items-center justify-center dark:bg-muted">
                  <FileText className="w-8 h-8 text-slate-400 dark:text-muted-foreground" />
                </div>
              )}

              <div className="absolute top-1 right-1 flex gap-1">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center"
                  onClick={() => setSelectedFile(file)}
                >
                  <Eye className="w-3 h-3" />
                </Button>
                {!readOnly && (
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center"
                    onClick={() => removeFile(file.id)}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                )}
              </div>

              <div className="mt-1 space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-xs font-medium text-slate-700 truncate dark:text-foreground">
                    {file.originalName}
                  </div>
                  <Badge variant="outline" className="text-[11px]">
                    {formatFileSize(file.fileSize)}
                  </Badge>
                </div>
                <OptimizedTextarea
                  id={`file-desc-${file.id}`}
                  value={
                    imageDescriptions[file.id] ?? file?.fileDescription ?? ""
                  }
                  readOnly={readOnly}
                  onChange={(value) => updateImageDescription(file.id, value)}
                  placeholder="Describe this attachment..."
                  rows={1}
                />
                {(file.uploadedDate || file.uploadDate) && (
                  <div className="text-xs text-slate-500 dark:text-muted-foreground">
                    Date:{" "}
                    {new Date(
                      file.uploadedDate || file.uploadDate,
                    ).toLocaleDateString()}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        {!readOnly && (
          <ImageCapturePicker
            buttonText="Add Documents & Photos"
            accept="image/*,.pdf,.doc,.docx,.xlsx,.pptx"
            isUploading={isUploading}
            disabled={loading}
            onConfirm={(selectedFiles) =>
              handleFileUpload(selectedFiles, filterData, entityId)
            }
          />
        )}
      </div>
    );
  };

  const renderActivityCard = () => {
    return (
      <div className="space-y-4">
        {filteredActivities?.map((activity) => {
          const currentActivityName = getWorkValue(activity, "name");
          const isOtherType = activity.workType === "other";

          return (
            <Card key={activity.id} className="p-4">
              <div className="space-y-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="font-semibold text-lg">
                      {getWorkValue(activity, "name") || "Untitled Activity"}{" "}
                      {/* ( {getWorkValue(activity, "workType") || "teset"} ) */}
                    </h4>
                  </div>
                  {!readOnly && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeActivity(activity.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>

                <div>
                  {isOtherType ? (
                    <div className="space-y-2">
                      <OptimizedInput
                        label="Scope"
                        id={`activityScope-${activity.id}`}
                        value={getWorkValue(activity, "name") || ""}
                        onChange={(val) =>
                          updateActivity(activity.id, "name", val)
                        }
                        placeholder="Enter scope for this activity"
                        required
                        readOnly={readOnly}
                      />
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <OptimizedSelect
                        label="Scope"
                        id={`activityName-${activity.id}`}
                        value={getWorkValue(activity, "name")}
                        onChange={(value: string) => {
                          if (value === "custom") {
                            toggleCustomInput(activity.id);
                            updateActivity(activity.id, "name", "");
                          } else {
                            updateActivity(activity.id, "name", value);
                            setShowCustomInput((prev) => ({
                              ...prev,
                              [activity.id]: false,
                            }));
                          }
                        }}
                        placeholder="Select Activity"
                        options={getActivityOptions(activity.workType)}
                        required
                        readOnly={readOnly}
                      />
                      {showCustomInput[activity.id] && (
                        <OptimizedInput
                          id={`customActivity-${activity.id}`}
                          value={getWorkValue(activity, "name")}
                          onChange={(val) =>
                            updateActivity(activity.id, "name", val)
                          }
                          placeholder="Enter custom activity name..."
                          required
                          readOnly={readOnly}
                        />
                      )}
                    </div>
                  )}

                  {!isOtherType &&
                  activity.workType === "mechanical" &&
                  currentActivityName === "Purlin and other attachments" ? (
                    <div className="space-y-3">
                      <Label className="mt-3">
                        Related Works <span className="text-red-500">*</span>
                      </Label>
                      {[
                        "All appropriate nuts & bolts are installed and appropriate torque applied",
                        "Nuts and bolts were installed and torque marked",
                        "No visible deformation was apparent on the structure",
                        "Synchronized system components",
                        "Any visible deformation",
                      ].map((option) => {
                        const currentRelated =
                          getWorkValue(activity, "relatedWorks") || [];

                        const checked = currentRelated.includes(option);

                        return (
                          <div
                            key={option}
                            className="flex items-center space-x-2"
                          >
                            <Checkbox
                              id={`welfare-${option}-${activity.id}`}
                              checked={checked}
                              onCheckedChange={(val) => {
                                const isChecked = Boolean(val);
                                const next = isChecked
                                  ? [...currentRelated, option]
                                  : currentRelated.filter(
                                      (o: string) => o !== option,
                                    );
                                updateActivity(
                                  activity.id,
                                  "relatedWorks",
                                  next,
                                );
                              }}
                            />
                            <Label
                              htmlFor={`welfare-${option}-${activity.id}`}
                              className="text-sm"
                            >
                              {option}
                            </Label>
                          </div>
                        );
                      })}
                    </div>
                  ) : currentActivityName ===
                    "Quality of piling/ramming the post" ? (
                    <div className="space-y-3">
                      <Label className="mt-3">
                        Related Works <span className="text-red-500">*</span>
                      </Label>
                      {["Anchoring depth", "Alignment / twisted?"].map(
                        (option) => {
                          const currentRelated =
                            getWorkValue(activity, "relatedWorks") || [];
                          const checked = currentRelated.includes(option);

                          return (
                            <div
                              key={option}
                              className="flex items-center space-x-2"
                            >
                              <Checkbox
                                id={`piling-${option}-${activity.id}`}
                                checked={checked}
                                onCheckedChange={(val) => {
                                  const isChecked = Boolean(val);
                                  const next = isChecked
                                    ? [...currentRelated, option]
                                    : currentRelated.filter(
                                        (o: string) => o !== option,
                                      );
                                  updateActivity(
                                    activity.id,
                                    "relatedWorks",
                                    next,
                                  );
                                }}
                              />
                              <Label
                                htmlFor={`piling-${option}-${activity.id}`}
                                className="text-sm"
                              >
                                {option}
                              </Label>
                            </div>
                          );
                        },
                      )}
                    </div>
                  ) : currentActivityName === "Mechanical corrosion" ? (
                    <div className="space-y-3">
                      <Label className="mt-3">
                        Related Works <span className="text-red-500">*</span>
                      </Label>
                      {["Mounting structure", "Mid clamps", "End clamps"].map(
                        (option) => {
                          const currentRelated =
                            getWorkValue(activity, "relatedWorks") || [];
                          const checked = currentRelated.includes(option);

                          return (
                            <div
                              key={option}
                              className="flex items-center space-x-2"
                            >
                              <Checkbox
                                id={`corrosion-${option}-${activity.id}`}
                                checked={checked}
                                onCheckedChange={(val) => {
                                  const isChecked = Boolean(val);
                                  const next = isChecked
                                    ? [...currentRelated, option]
                                    : currentRelated.filter(
                                        (o: string) => o !== option,
                                      );
                                  updateActivity(
                                    activity.id,
                                    "relatedWorks",
                                    next,
                                  );
                                }}
                              />
                              <Label
                                htmlFor={`corrosion-${option}-${activity.id}`}
                                className="text-sm"
                              >
                                {option}
                              </Label>
                            </div>
                          );
                        },
                      )}
                    </div>
                  ) : null}

                  {!isOtherType &&
                  activity.workType === "civil" &&
                  currentActivityName === "Foundations" ? (
                    <div className="space-y-3">
                      <Label className="mt-3">
                        Related Works <span className="text-red-500">*</span>
                      </Label>
                      {[
                        "Fence",
                        "CCTV",
                        "Combiner box / LV feeder pillar",
                        "Inverter",
                        "Transformer",
                        "Switchgear",
                      ].map((option) => {
                        const currentRelated =
                          getWorkValue(activity, "relatedWorks") || [];
                        const checked = currentRelated.includes(option);

                        return (
                          <div
                            key={option}
                            className="flex items-center space-x-2"
                          >
                            <Checkbox
                              id={`foundation-${option}-${activity.id}`}
                              checked={checked}
                              onCheckedChange={(val) => {
                                const isChecked = Boolean(val);
                                const next = isChecked
                                  ? [...currentRelated, option]
                                  : currentRelated.filter(
                                      (o: string) => o !== option,
                                    );
                                updateActivity(
                                  activity.id,
                                  "relatedWorks",
                                  next,
                                );
                              }}
                            />
                            <Label
                              htmlFor={`foundation-${option}-${activity.id}`}
                              className="text-sm"
                            >
                              {option}
                            </Label>
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                </div>

                <div className="space-y-2">
                  <OptimizedTextarea
                    label="Commentary"
                    id={`description-${activity.id}`}
                    value={getWorkValue(activity, "comments")}
                    onChange={(val) =>
                      updateActivity(activity.id, "comments", val)
                    }
                    placeholder="Describe the activity in detail, including observations and findings..."
                    rows={3}
                    readOnly={readOnly}
                    required
                  />
                </div>

                {renderFileAttachments(
                  { [activity.id]: true },
                  activity.id,
                  true,
                )}
              </div>
            </Card>
          );
        })}
      </div>
    );
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {!readOnly && (
        <LocalSectionImportControl
          previousCompletedReports={previousCompletedReports}
          sectionName="key-activities-performed"
          siteId={siteId}
          reportId={reportId || undefined}
          readOnly={readOnly}
          setFormData={setFormData}
          workProgressData={workProgressData}
          setWorkProgressData={setWorkProgressData}
        />
      )}
      {/* Floating save section with tooltip */}
      {!readOnly && (
        <div className="fixed top-25 right-3 z-40 sm:right-4">
          <div className="flex flex-row items-center gap-2 sm:flex-col sm:items-end sm:gap-3">
            {/* Status indicator - same size as save button */}
            {false && (
              <div
                className="
          bg-white/95 backdrop-blur-sm
          rounded-full border-2 border-gray-200/80
          shadow-lg
          w-12 h-12 sm:w-16 sm:h-16
          flex items-center justify-center
          group relative
          transition-all duration-300 ease-out
          transform hover:scale-110
          cursor-default
          p-2
        "
              >
                <div className="flex flex-col items-center justify-center w-full h-full">
                  <span className="text-[10px] font-medium text-gray-600 leading-none mb-2 dark:text-muted-foreground">
                    Saved
                  </span>

                  <div className="flex items-center justify-center h-6">
                    <LoadingIndicator
                      status={getLoadingStatus()}
                      lastSaved={lastSaved}
                      error={saveError}
                    />
                  </div>
                </div>

                {/* Tooltip - Fixed with pointer-events-none */}
                <div className="absolute right-full mr-2 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                  {getLoadingStatus() === "saving"
                    ? "Saving..."
                    : getLoadingStatus() === "saved"
                      ? `Saved ${lastSaved?.toLocaleTimeString()}`
                      : getLoadingStatus() === "error"
                        ? "Save failed"
                        : "Ready to save"}
                </div>

                {/* Error indicator */}
                {getLoadingStatus() === "error" && (
                  <div className="absolute -top-1 -right-1">
                    <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                    <div className="w-3 h-3 bg-red-500 rounded-full absolute top-0 left-0 border-2 border-white"></div>
                  </div>
                )}
              </div>
            )}

            {/* Save button - Fixed with pointer-events-none */}
            <Button
              onClick={forceSave}
              disabled={isSaving || readOnly}
              className={`
          relative rounded-full w-12 h-12 sm:w-16 sm:h-16
          transition-all duration-300 ease-out
          flex flex-col items-center justify-center
          backdrop-blur-sm border-2
          shadow-lg hover:shadow-xl
          transform hover:scale-110 active:scale-95
          group
          ${
            hasUnsavedChanges && !readOnly
              ? "bg-gradient-to-br from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white border-red-300 dark:bg-gradient-to-br dark:border-red-300 dark:ring-red-300"
              : "bg-white/95 text-gray-600 border-gray-200/80 hover:bg-white/90 dark:bg-muted dark:text-muted-foreground dark:border-border dark:hover:bg-accent"
          }
          ${isSaving || readOnly ? "opacity-80 cursor-not-allowed" : "cursor-pointer"}
        `}
            >
              {isSaving ? (
                <div className="flex flex-col items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mb-2"></div>
                  <span className="text-[10px] leading-none">Saving</span>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center">
                  <Save className="w-5 h-5 sm:mb-1" />
                  <span className="hidden text-[10px] font-medium leading-none sm:block">
                    Save
                  </span>
                </div>
              )}

              {/* Unsaved changes indicator */}
              {hasUnsavedChanges && !isSaving && (
                <div className="absolute -top-1 -right-1">
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-ping"></div>
                  <div className="w-3 h-3 bg-red-500 rounded-full absolute top-0 left-0 border-2 border-white"></div>
                </div>
              )}

              {/* Tooltip - Fixed with pointer-events-none */}
              <div className="absolute right-full mr-2 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                {hasUnsavedChanges ? "Save changes" : "No changes to save"}
              </div>
            </Button>
          </div>
        </div>
      )}

      {/* Activities Tabs */}

      {/* <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="mechanical">Mechanical</TabsTrigger>
          <TabsTrigger value="electrical">Electrical</TabsTrigger>
          <TabsTrigger value="civil">Civil</TabsTrigger>
        </TabsList> */}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>All Key Activities</CardTitle>
            {!readOnly && (
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  onClick={() => addActivity("mechanical")}
                  variant="outline"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Mechanical
                </Button>
                <Button
                  size="sm"
                  onClick={() => addActivity("electrical")}
                  variant="outline"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Electrical
                </Button>
                <Button
                  size="sm"
                  onClick={() => addActivity("civil")}
                  variant="outline"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Civil
                </Button>
                <Button
                  size="sm"
                  onClick={() => addActivity("other")}
                  variant="outline"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Other
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <Tabs
            value={activeTab}
            onValueChange={(value: any) => setActiveTab(value)}
          >
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="all">
                All -{" "}
                {currentWorksData?.filter(
                  (work) => work.workCategory === "key-activities-performed",
                ).length || 0}
              </TabsTrigger>
              <TabsTrigger value="mechanical">
                Mechanical -{" "}
                {currentWorksData?.filter(
                  (work) =>
                    work.workCategory === "key-activities-performed" &&
                    work?.workType === "mechanical",
                ).length || 0}
              </TabsTrigger>
              <TabsTrigger value="electrical">
                Electrical -{" "}
                {currentWorksData?.filter(
                  (work) =>
                    work.workCategory === "key-activities-performed" &&
                    work?.workType === "electrical",
                ).length || 0}
              </TabsTrigger>
              <TabsTrigger value="civil">
                Civil -{" "}
                {currentWorksData?.filter(
                  (work) =>
                    work.workCategory === "key-activities-performed" &&
                    work?.workType === "civil",
                ).length || 0}
              </TabsTrigger>
              <TabsTrigger value="other">
                Other -{" "}
                {currentWorksData?.filter(
                  (work) =>
                    work.workCategory === "key-activities-performed" &&
                    work?.workType === "other",
                ).length || 0}
              </TabsTrigger>
            </TabsList>

            <TabsContent key={activeTab} value={activeTab} className="mt-6">
              {filteredActivities?.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-slate-500 dark:text-muted-foreground">
                    No {activeTab} activities yet
                  </p>
                </div>
              ) : (
                <div className="space-y-4">{renderActivityCard()}</div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* {(["mechanical", "electrical", "civil"] as const).map((category) => (
          <TabsContent key={category} value={category} className="mt-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>
                    {category.charAt(0).toUpperCase() + category.slice(1)}{" "}
                    Activities
                  </CardTitle>
                  {!readOnly && (
                    <Button size="sm" onClick={() => addActivity(category)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Add Activity
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {filteredActivities?.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-slate-500">
                      No {category} activities yet
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredActivities?.map((activity) =>
                      renderActivityCard(activity, category),
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        ))} */}

      {/* Overall Comment */}
      <Card>
        <CardHeader>
          {/* <CardTitle>Overall Summary & Comments</CardTitle> */}
        </CardHeader>
        <CardContent className="space-y-6">
          <OptimizedTextarea
            label="Overall Summary & Conclusions"
            id="overallSummary"
            value={formData.overallSummary}
            readOnly={readOnly}
            onChange={(value) =>
              setFormData((prev) => ({ ...prev, overallSummary: value }))
            }
            placeholder="Provide an overall summary and conclusions about all key activities performed..."
            rows={4}
            required
          />

          {/* {renderFileAttachments({ key_activities_summary: true })} */}
        </CardContent>
      </Card>

      {/* File Preview Modal */}
      {selectedFile && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl max-h-full overflow-auto dark:bg-card">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">
                {selectedFile.originalName}
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedFile(null)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="p-4">
              {selectedFile?.fileType?.startsWith("image/") ? (
                <OfflineImage
                  fileId={
                    selectedFile?._id ||
                    selectedFile?.id ||
                    selectedFile?._clientId
                  }
                  src={selectedFile?.fileUrl || "/placeholder.svg"}
                  alt={selectedFile?.originalName}
                  className="max-w-full max-h-96 mx-auto"
                />
              ) : (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 text-slate-400 mx-auto mb-4 dark:text-muted-foreground" />
                  <p className="text-slate-600 dark:text-muted-foreground">
                    Preview not available for this file type
                  </p>
                  <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => window.open(selectedFile.fileUrl, "_blank")}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download File
                  </Button>
                </div>
              )}
              <div className="mt-4 text-sm text-slate-500 dark:text-muted-foreground">
                <p>Size: {formatFileSize(selectedFile.fileSize)}</p>
                <p>
                  Uploaded:{" "}
                  {new Date(selectedFile.uploadDate).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sticky Bottom Bar */}
      <div className="sticky bottom-2 md:bottom-4 z-20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3 md:p-4 bg-white dark:bg-card rounded-lg border shadow-lg backdrop-blur-sm bg-white/95 dark:bg-surface-raised/95">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-slate-700 dark:text-muted-foreground">
            Completion:
          </span>
          <Badge variant="outline">
            {punchListKeyActivitiesPerformedProgress(
              currentWorksData?.filter(
                (w) => w.workCategory === "key-activities-performed",
              ) || [],
              currentSiteData?.punchList?.keyActivitiesPerformed
                ?.overallSummary,
            )}
            %
          </Badge>
        </div>
        <div className="text-xs md:text-sm text-slate-500 dark:text-muted-foreground hidden lg:block">
          Auto-save enabled • Changes saved automatically
        </div>
      </div>
    </div>
  );
});
